import React, { useMemo, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Heart, ShoppingCart, ChevronLeft, ChevronRight, ArrowDownSquare, ArrowBigRight } from 'lucide-react';
import './pop.css';
import './ProductDetail.css'
import POP from '../../images/pop.jpg';
import { useCart } from "../../context/CartContext";
import { useFavorites } from "../../context/FavoritesContext";
import { Routes, Route, useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { fetchProducts, fetchProductById, fetchRelatedProducts } from "../../api/products.api";
import ShareButton from "../../Components/ShareButton";
import { getNewestItemRank } from "../../utils/sortDisplayOrder";


const FALLBACK_BANNER_BG =
  "https://hips.hearstapps.com/hmg-prod/images/2022-chevrolet-corvette-z06-1607016574.jpg?crop=0.800xw:0.601xh;0.150xw,0.318xh&resize=1200:*";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const DEFAULT_MAX_PRICE = 6000;

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const isLocalHost = (hostname = "") =>
  hostname === "127.0.0.1" ||
  hostname === "localhost" ||
  hostname === "0.0.0.0";

const toAbsoluteUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const fallbackBase =
    API_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");

  try {
    const parsed = new URL(raw, fallbackBase || undefined);

    // Fix backend media URLs that point to localhost in production payloads.
    if (API_BASE_URL && isLocalHost(parsed.hostname)) {
      const apiUrl = new URL(API_BASE_URL);
      parsed.protocol = apiUrl.protocol;
      parsed.host = apiUrl.host;
    }

    // Avoid mixed-content blocking on HTTPS deployments.
    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      parsed.protocol === "http:"
    ) {
      parsed.protocol = "https:";
    }

    return parsed.toString();
  } catch {
    if (!API_BASE_URL) return raw;
    return raw.startsWith("/") ? `${API_BASE_URL}${raw}` : `${API_BASE_URL}/${raw}`;
  }
};

const resolveDisplayPoints = (item, isMember) => {
  const basePoints = Number(item?.points ?? item?.Points);
  const memberPoints = Number(item?.member_points ?? item?.memberPoints);

  if (isMember) {
    return Number.isFinite(memberPoints) && memberPoints > 0
      ? memberPoints
      : 0;
  }

  return Number.isFinite(basePoints) && basePoints > 0
    ? basePoints
    : 0;
};



const DiecastStore = () => {
  const { name } = useParams();
  const {
    updateCart,
    getProductQuantity,
    isProductInCart,
  } = useCart();

  const [currentPage, setCurrentPage] = useState("listing");
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);

  const { isAuthenticated, setShowLoginModal, isMember } =
    useAuth();

  const location = useLocation();
  const navigate = useNavigate();

  // ================= FILTER STATE =================
  const [sortBy, setSortBy] = useState("recommended");
  const [priceRange, setPriceRange] = useState([0, DEFAULT_MAX_PRICE]);
  const [selectedDiscounts, setSelectedDiscounts] = useState(
    new Set()
  );
  const [activePage, setActivePage] = useState(1);


  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const { toggleFavorite, isFavorite } = useFavorites();

  const sliderMaxPrice = useMemo(
    () =>
      Math.max(
        DEFAULT_MAX_PRICE,
        ...products
          .map((product) => toFiniteNumber(product.price))
          .filter((price) => price > 0)
      ),
    [products]
  );

  useEffect(() => {
    let mounted = true;

    fetchProducts()
      .then((data) => {
        if (!mounted) return;

        const mapped = data.products.map((p) => {
          const brandLabel = String(p.category_name || "").trim();
          const brandKey = brandLabel.toLowerCase();
          const selectionImages = Array.isArray(p.selections)
            ? p.selections
              .map((s) => s?.images?.medium)
              .filter(Boolean)
            : [];

          let images = selectionImages.slice(0, 3);
          while (images.length < 3) {
            images.push(p.image);
          }

          return {
            ...p,
            price: toFiniteNumber(p.price),
            brand: brandKey,
            brandLabel,
            mrp: Number(p.cost_price) || null,
            category_image: toAbsoluteUrl(p.category_image),
            category_background_image: toAbsoluteUrl(
              p.category_background_image
            ),
            images,
            details: p.description
              ? Array.isArray(p.description)
                ? p.description
                : [p.description]
              : [],
            is_new_arrival: p.is_new_arrival,
            points: p.points
          };
        });

        setProducts(mapped);
        const maxProductPrice = mapped.reduce(
          (max, product) => Math.max(max, toFiniteNumber(product.price)),
          DEFAULT_MAX_PRICE
        );
        setPriceRange([0, maxProductPrice]);

        const uniqueBrands = Array.from(
          mapped.reduce((brandMap, product) => {
            if (!product.brand) return brandMap;

            if (!brandMap.has(product.brand)) {
              brandMap.set(product.brand, {
                name: product.brand,
                label: product.brandLabel || product.brand,
                count: 0,
              });
            }

            brandMap.get(product.brand).count += 1;
            return brandMap;
          }, new Map()).values()
        );

        setBrands(uniqueBrands);
        if (name) {
          setSelectedBrand(name.toLowerCase());
        } else {
          setSelectedBrand(null);
        }

        if (uniqueBrands.length) {
          setSelectedBrand(
            (prev) => prev || uniqueBrands[0].name
          );
        }
      })
      .catch(() => {
        if (!mounted) return;
        setProducts([]);
        setBrands([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);
  useEffect(() => {
    if (!brands.length) return;

    if (name) {
      const normalized = name.toLowerCase();
      const exists = brands.some((b) => b.name === normalized);
      setSelectedBrand(exists ? normalized : null);
    } else {
      setSelectedBrand(null);
    }

    // optional reset when changing brand
    setCurrentPage("listing");
    setSortBy("recommended");
    setPriceRange([0, sliderMaxPrice]);
    setSelectedDiscounts(new Set());
    setActivePage(1);
  }, [name, brands, sliderMaxPrice]);

  const viewProduct = (product) => {
    setSelectedImage(0);
    navigate(`product/${product.id}`);
  };

  const handleAddToCart = (product) => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (product.max_unit && product.max_unit <= 0) return;

    updateCart({
      type: "product",
      id: product.id,
      delta: 1,
    });
  };

  const handleBrandClick = (brandName) => {
    if (brandName === "ALL") {
      setSelectedBrand(null);
    } else {
      setSelectedBrand(brandName);
    }
    setCurrentPage("listing");

    setSortBy("recommended");
    setPriceRange([0, sliderMaxPrice]);
    setSelectedDiscounts(new Set());
    setActivePage(1);
  };

  const ProductListing = () => {
    const ITEMS_PER_PAGE = 20;
    const [showMaxDialog, setShowMaxDialog] = useState(false);
    const [tempPriceRange, setTempPriceRange] = useState(priceRange);
    const handleDiscountChange = (discountRange) => {
      const newDiscounts = new Set(selectedDiscounts);
      newDiscounts.has(discountRange)
        ? newDiscounts.delete(discountRange)
        : newDiscounts.add(discountRange);

      setSelectedDiscounts(newDiscounts);
      setActivePage(1);
    };

    useEffect(() => {
      setTempPriceRange(priceRange);
    }, [priceRange]);
    const getDiscountPercentage = (mrp, price) => {
      if (!mrp || mrp <= price) return 0;
      return ((mrp - price) / mrp) * 100;
    };

    const matchesDiscountFilter = (product) => {
      if (selectedDiscounts.size === 0) return true;

      const discount = getDiscountPercentage(product.mrp, product.price);

      for (let range of selectedDiscounts) {
        if (range === "Upto 10%" && discount <= 10) return true;
        if (range === "10% - 20%" && discount > 10 && discount <= 20) return true;
        if (range === "20% - 30%" && discount > 20 && discount <= 30) return true;
        if (range === "30% - 40%" && discount > 30 && discount <= 40) return true;
        if (range === "40%-50%" && discount > 40 && discount <= 50) return true;
      }
      return false;
    };

    const filteredProducts = products.filter((product) => {

      if (selectedBrand && product.brand !== selectedBrand) return false;


      if (product.price < priceRange[0] || product.price > priceRange[1])
        return false;

      if (!matchesDiscountFilter(product)) return false;

      return true;
    });


    const sortedProducts = [...filteredProducts].sort((a, b) => {
      if (sortBy === "recommended") {
        return getNewestItemRank(b) - getNewestItemRank(a);
      }
      if (sortBy === "price-low") return a.price - b.price;
      if (sortBy === "price-high") return b.price - a.price;
      if (sortBy === "date-new") {
        return getNewestItemRank(b) - getNewestItemRank(a);
      }
      if (sortBy === "date-old") {
        return getNewestItemRank(a) - getNewestItemRank(b);
      }
      return 0;
    });

    const totalPages = Math.ceil(sortedProducts.length / ITEMS_PER_PAGE);
    const startIndex = (activePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentProducts = sortedProducts.slice(startIndex, endIndex);

    const goToPage = (page) => {
      setActivePage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const goToPrevPage = () => activePage > 1 && goToPage(activePage - 1);
    const goToNextPage = () => activePage < totalPages && goToPage(activePage + 1);

    const getPageNumbers = () => {
      const pages = [];
      const max = 5;

      if (totalPages <= max) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else if (activePage <= 3) {
        for (let i = 1; i <= 5; i++) pages.push(i);
      } else if (activePage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        for (let i = activePage - 2; i <= activePage + 2; i++) pages.push(i);
      }
      return pages;
    };

    const capitalizeWords = (text) => {
      if (!text) return "All";

      return text
        .toLowerCase()
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const selectedBrandLabel = selectedBrand
      ? brands.find((brand) => brand.name === selectedBrand)?.label ||
      capitalizeWords(selectedBrand)
      : "All";

    const activeBrandAssets = selectedBrand
      ? products.find(
        (p) =>
          p.brand === selectedBrand &&
          (p.category_image || p.category_background_image)
      ) || products.find((p) => p.brand === selectedBrand)
      : null;
    const bannerBackgroundImage =
      activeBrandAssets?.category_background_image || FALLBACK_BANNER_BG;


    if (loading) {
      return (
        <div className="loading-wrap">
          <div className="loader" />
          <p>Loading combos…</p>
        </div>
      );
    }


    return (
      <div className="page">
        <div
          className="banner"
          style={{ backgroundImage: `url(${bannerBackgroundImage})` }}
        >
          <div className="banner-container">
            <div className="banner-box">
              <div className="banner-text">
                <h1 className="banner-brand-title">{selectedBrandLabel.toUpperCase()}</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="breadcrumb11">
          <div className="page-container">
            <span onClick={() => navigate("/")}>Home</span>
            <span className="highlight"> / </span>{" "}
            <span>{selectedBrandLabel}</span>
            <span className="highlight"> / </span>{" "}
            {sortedProducts.length}
          </div>

        </div>

        <div className="main-content">
          <div className="content-wrapper">
            <div className="sidebar">
              <div className="filter-box">
                <h3 className="filter-title">BRANDS</h3>

                <div className="filter-options">

                  <div
                    className={`brand-nav-item ${selectedBrand === null ? "brand-nav-active" : ""
                      }`}
                    onClick={() => handleBrandClick("ALL")}
                  >
                    <span>ALL</span>
                    <span className="filter-count">
                      {brands.reduce((sum, b) => sum + b.count, 0)}
                    </span>
                  </div>


                  {/* brands */}
                  {brands.map((brand) => (
                    <div
                      key={brand.name}
                      className={`brand-nav-item ${selectedBrand === brand.name ? "brand-nav-active" : ""
                        }`}
                      onClick={() => handleBrandClick(brand.name)}
                    >
                      <span>{brand.label || capitalizeWords(brand.name)}</span>
                      <span className="filter-count">{brand.count}</span>
                    </div>
                  ))}
                </div>
              </div>


              <div className="filter-box">
                <h3 className="filter-title">PRICES</h3>
                <div className="price-range">
                  <span className="price-label">Range:</span>
                  ₹{priceRange[0]} - ₹{priceRange[1]}
                </div>

                <div className="dual-slider">
                  {/* LEFT */}
                  <input
                    type="range"
                    min="0"
                    max={sliderMaxPrice}
                    step="10"
                    value={tempPriceRange[0]}
                    onChange={(e) => {
                      const value = Math.min(Number(e.target.value), tempPriceRange[1] - 100);
                      setTempPriceRange([value, tempPriceRange[1]]);
                    }}
                    onMouseUp={() => {
                      setPriceRange(tempPriceRange);
                      setActivePage(1);
                    }}
                    onTouchEnd={() => {
                      setPriceRange(tempPriceRange);
                      setActivePage(1);
                    }}
                    className="thumb thumb-left"
                  />

                  {/* RIGHT */}
                  <input
                    type="range"
                    min="0"
                    max={sliderMaxPrice}
                    step="10"
                    value={tempPriceRange[1]}
                    onChange={(e) => {
                      const value = Math.max(Number(e.target.value), tempPriceRange[0] + 100);
                      setTempPriceRange([tempPriceRange[0], value]);
                    }}
                    onMouseUp={() => {
                      setPriceRange(tempPriceRange);
                      setActivePage(1);
                    }}
                    onTouchEnd={() => {
                      setPriceRange(tempPriceRange);
                      setActivePage(1);
                    }}
                    className="thumb thumb-right"
                  />

                  <div className="slider-track" />
                  <div
                    className="slider-range"
                    style={{
                      left: `${(tempPriceRange[0] / sliderMaxPrice) * 100}%`,
                      right: `${100 - (tempPriceRange[1] / sliderMaxPrice) * 100}%`
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="products-section">
              <div className="mobile-filter-bar">
                <span className="mobile-filter-label">Refine</span>

                <button
                  className="mobile-filter-btn"
                  onClick={() => setFilterOpen(true)}
                >
                  Filters
                </button>
              </div>

              <div className="sort-bar">
                <div className="sort-label">Sort By:</div>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setActivePage(1);
                  }}
                  className="sort-select"
                >
                  <option value="recommended">Recommended</option>
                  <option value="price-high">Price, High To Low</option>
                  <option value="price-low">Price, Low To High</option>
                  <option value="date-new">Date, New to Old</option>
                  <option value="date-old">Date, Old to New</option>
                </select>
              </div>

              <div className="products-grid">
                {currentProducts.length > 0 ? (

                  currentProducts.map((product) => {
                    const currentQty = getProductQuantity(product.id);
                    const maxUnit = product.max_unit;
                    const isSoldOut = maxUnit === 0;
                    const isInCart = currentQty > 0;
                    const displayPoints = resolveDisplayPoints(product, isMember);
                    const showPointsBadge = displayPoints > 0;
                    const isMaxReached =
                      maxUnit > 0 && currentQty >= maxUnit;

                    const handleIncreaseQty = () => {
                      if (maxUnit > 0 && currentQty >= maxUnit) {
                        setShowMaxDialog(true);
                        return;
                      }

                      updateCart({
                        type: "product",
                        id: product.id,
                        delta: 1,
                      });
                    };


                    return (
                      <div key={product.id} className="product-card">
                        <div className="product-image-wrapper">
                          {product.is_new_arrival ? (
                            <div className="product-badge new">New Arrival</div>
                          ) : product.is_trending ? (
                            <div className="product-badge trending">Trending Now</div>
                          ) : null}



                          {showPointsBadge && (
                            <div className="product-points-badge">
                              ₹{displayPoints} Rewards
                            </div>
                          )}

                          <img
                            src={product.image}
                            alt={product.name}
                            className="product-image"
                            onClick={() => viewProduct(product)}
                          />
                        </div>

                        <div className="product-info">


                          <h4 className="product-name" onClick={() => viewProduct(product)}>
                            {product.name}
                          </h4>
                          <div className="product-brand">{product.brandLabel || product.brand} {product.variant}</div>
                          {isMember && product.mrp > product.price ? (
                            <>
                              <div className="product-mrp">
                                MRP: <span className="strikethrough">₹{product.mrp}</span>
                              </div>

                              <div className="product-pricing">
                                <span className="product-price">₹{product.price}</span>

                                {getDiscountPercentage(product.mrp, product.price) >= 10 && (
                                  <span className="product-discount">
                                    {Math.round(
                                      getDiscountPercentage(product.mrp, product.price)
                                    )}% OFF
                                  </span>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="product-pricing">
                              <span className="product-price">₹{product.price}</span>
                            </div>
                          )}

                          <div className="product-actions">
                            {/*SOLD OUT */}
                            {isSoldOut && (
                              <button className="add-to-cart-btn" disabled>
                                SOLD OUT
                              </button>
                            )}

                            {/*ADD TO CART */}
                            {!isSoldOut && !isInCart && (
                              <button
                                className="add-to-cart-btn"
                                onClick={() => handleAddToCart(product)}
                              >
                                <ShoppingCart size={16} />
                                Add To Cart
                              </button>
                            )}

                            {/*QUANTITY SELECTOR */}
                            {!isSoldOut && isInCart && (
                              <div className="quantity-selector">
                                <button
                                  className="quantity-btn"
                                  onClick={() =>
                                    updateCart({
                                      type: "product",
                                      id: product.id,
                                      delta: -1,
                                    })
                                  }
                                >
                                  -
                                </button>

                                <span className="quantity-value">{currentQty}</span>

                                <button
                                  className="quantity-btn"
                                  onClick={() =>
                                    updateCart({
                                      type: "product",
                                      id: product.id,
                                      delta: 1,
                                    })
                                  }
                                  disabled={isMaxReached}
                                  style={{
                                    opacity: isMaxReached ? 0.5 : 1,
                                    cursor: isMaxReached ? "not-allowed" : "pointer",
                                  }}
                                >
                                  +
                                </button>
                              </div>
                            )}


                            {/* Wishlist stays same */}
                            <button
                              className={`pd-favv ${isFavorite(product.id) ? "active" : ""}`}
                              onClick={() => toggleFavorite(product.id)}
                            >
                              <Heart
                                size={22}
                                fill={isFavorite(product.id) ? "currentColor" : "none"}
                              />
                            </button>


                          </div>


                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-products">
                    <p>No products found matching your filters.</p>
                  </div>
                )}
              </div>


              {sortedProducts.length > 0 && totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={goToPrevPage}
                    disabled={activePage === 1}
                    style={{
                      opacity: activePage === 1 ? 0.5 : 1,
                      cursor: activePage === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <ChevronLeft size={18} />
                  </button>

                  {getPageNumbers().map((pageNum) => (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${activePage === pageNum ? 'pagination-active' : ''}`}
                      onClick={() => goToPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  ))}

                  <button
                    className="pagination-btn"
                    onClick={goToNextPage}
                    disabled={activePage === totalPages}
                    style={{
                      opacity: activePage === totalPages ? 0.5 : 1,
                      cursor: activePage === totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}

              {sortedProducts.length > 0 && (
                <div style={{
                  textAlign: 'center',
                  marginTop: '16px',
                  color: '#6b7280',
                  fontSize: '14px'
                }}>
                  Showing {startIndex + 1} - {Math.min(endIndex, sortedProducts.length)} of {sortedProducts.length} products
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={`mobile-filter-drawer ${filterOpen ? "open" : ""}`}>
          <div className="drawer-header">
            <span>Filters</span>
            <button onClick={() => setFilterOpen(false)}>✕</button>
          </div>

          <div className="drawer-body">
            <div className="sidebar">
              <div className="filter-box">
                <h3 className="filter-title">BRANDS</h3>
                <div className="filter-options">
                  {brands.map((brand) => (
                    <div
                      key={brand.name}
                      className={`brand-nav-item ${selectedBrand === brand.name ? 'brand-nav-active' : ''}`}
                      onClick={() => handleBrandClick(brand.name)}
                    >
                      <span>{brand.label || brand.name}</span>
                      <span className="filter-count">{brand.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="filter-box">
                <h3 className="filter-title">PRICES</h3>
                <div className="price-range">
                  <span className="price-label">Range:</span>
                  ₹{priceRange[0]} - ₹{priceRange[1]}
                </div>

                <div className="dual-slider">
                  <input
                    type="range"
                    min="0"
                    max={sliderMaxPrice}
                    step="100"
                    value={priceRange[0]}
                    onChange={(e) => {
                      const value = Math.min(Number(e.target.value), priceRange[1] - 100);
                      setPriceRange([value, priceRange[1]]);
                      setActivePage(1);
                    }}
                    className="thumb thumb-left"
                  />
                  <input
                    type="range"
                    min="0"
                    max={sliderMaxPrice}
                    step="100"
                    value={priceRange[1]}
                    onChange={(e) => {
                      const value = Math.max(Number(e.target.value), priceRange[0] + 100);
                      setPriceRange([priceRange[0], value]);
                      setActivePage(1);
                    }}
                    className="thumb thumb-right"
                  />
                  <div className="slider-track" />
                  <div
                    className="slider-range"
                    style={{
                      left: `${(priceRange[0] / sliderMaxPrice) * 100}%`,
                      right: `${100 - (priceRange[1] / sliderMaxPrice) * 100}%`
                    }}
                  />
                </div>
              </div>

            </div>
          </div>
        </div>

        <div
          className={`drawer-backdrop ${filterOpen ? "show" : ""}`}
          onClick={() => setFilterOpen(false)}
        />
      </div>

    );
  };



  const ProductDetail = ({ products, selectedBrand, loading }) => {
    const { isMember, isAuthenticated, setShowLoginModal } = useAuth();
    const { productId } = useParams();
    const navigate = useNavigate();

    const { updateCart, getProductQuantity, isProductInCart } = useCart();
    const { toggleFavorite, isFavorite } = useFavorites();

    const [selectedImage, setSelectedImage] = useState(0);
    const [isAdding, setIsAdding] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [loadingProduct, setLoadingProduct] = useState(true);
    const [relatedProducts, setRelatedProducts] = useState([]);
    useEffect(() => {
      const loadData = async () => {
        try {
          setLoadingProduct(true);

          // 1️⃣ Fetch product
          const data = await fetchProductById(productId);

          const orderedRowImages = Array.isArray(data.order_rows)
            ? [...data.order_rows]
              .filter((row) => row?.image && row.checked !== false)
              .sort(
                (a, b) =>
                  Number(a?.order_number || 0) - Number(b?.order_number || 0)
              )
              .map((row) => toAbsoluteUrl(row.image))
              .filter(Boolean)
            : [];

          const selectionImages = Array.isArray(data.selections)
            ? data.selections
              .map((s) => toAbsoluteUrl(s?.images?.medium))
              .filter(Boolean)
            : [];

          const fallbackImage = toAbsoluteUrl(data.image);
          const gallerySource = orderedRowImages.length
            ? orderedRowImages
            : selectionImages;
          const images = Array.from(
            new Set([fallbackImage, ...gallerySource].filter(Boolean))
          ).slice(0, 4);

          const formattedProduct = {
            ...data,
            image: fallbackImage,
            price: Number(data.price || 0),
            mrp: Number(data.cost_price || 0),
            brand: data.category_name
              ? data.category_name.trim().toLowerCase()
              : "",
            points: data.points,
            images,
            details: data.description
              ? Array.isArray(data.description)
                ? data.description
                : [data.description]
              : [],

          };

          setSelectedProduct(formattedProduct);

          // 2️⃣ Fetch related (after product is confirmed)
          const related = await fetchRelatedProducts(productId);

          const mappedRelated = related.map((p) => {
            const orderedRowImages = Array.isArray(p.order_rows)
              ? [...p.order_rows]
                .filter((row) => row?.image && row.checked !== false)
                .sort(
                  (a, b) =>
                    Number(a?.order_number || 0) - Number(b?.order_number || 0)
                )
                .map((row) => toAbsoluteUrl(row.image))
                .filter(Boolean)
              : [];

            const selectionImages = Array.isArray(p.selections)
              ? p.selections
                .map((s) => toAbsoluteUrl(s?.images?.medium))
                .filter(Boolean)
              : [];

            const fallbackImage = toAbsoluteUrl(p.image);
            const images = orderedRowImages.length
              ? orderedRowImages
              : selectionImages.length
                ? selectionImages
                : [fallbackImage].filter(Boolean);

            return {
              ...p,
              image: fallbackImage,
              price: Number(p.price || 0),
              mrp: Number(p.cost_price || 0),
              brand: p.category_name
                ? p.category_name.trim().toLowerCase()
                : "",
              points:p.points,
              images,
            };
          });

          setRelatedProducts(mappedRelated);

        } catch (err) {
          console.error("Error loading product:", err);
          setSelectedProduct(null);
          setRelatedProducts([]);
        } finally {
          setLoadingProduct(false);
        }
      };

      loadData();
    }, [productId]);
    const images = useMemo(() => {
      if (!selectedProduct) return [];
      const list = selectedProduct.images?.length
        ? selectedProduct.images
        : [selectedProduct.image];
      return list.filter(Boolean);
    }, [selectedProduct]);

    const details = useMemo(
      () =>
        Array.isArray(selectedProduct?.details) ? selectedProduct.details : [],
      [selectedProduct]
    );

    const normalizedDetails = useMemo(
      () =>
        details.flatMap((d) =>
          d
            .split(/\n|\./)
            .map((s) => s.trim())
            .filter(Boolean)
        ),
      [details]
    );

    const cartQty = selectedProduct ? getProductQuantity(selectedProduct.id) : 0;
    const isInCart = selectedProduct
      ? isProductInCart(selectedProduct.id)
      : false;
    const maxUnit = selectedProduct?.max_unit ?? 0;
    const isSoldOut = maxUnit === 0;
    const productBrand = selectedProduct?.brand;
    const selectedPoints = resolveDisplayPoints(selectedProduct, isMember);
    const showSelectedPointsBadge = selectedPoints > 0;

    useEffect(() => {
      if (selectedProduct) setSelectedImage(0);
    }, [selectedProduct?.id]);

    useEffect(() => {
      if (isAdding && (isInCart || cartQty > 0)) {
        setIsAdding(false);
      }
    }, [isAdding, isInCart, cartQty]);

    const handleAddToCart = (product = selectedProduct) => {
      if (!product) return;
      if (!isAuthenticated) {
        setShowLoginModal(true);
        return;
      }
      if ((product.max_unit ?? 0) === 0) return;

      if (product.id === selectedProduct?.id) {
        setIsAdding(true);
      }
      updateCart({
        type: "product",
        id: product.id,
        delta: 1,
      });
    };

    if (loadingProduct) {
      return (
        <div className="page">
          <div className="loading-wrap">
            <div className="loader" />
            <div>Loading product...</div>
          </div>
        </div>
      );
    }
    if (!selectedProduct) {
      return (
        <div className="page">
          <div className="loading-wrap">
            <div>Product not found</div>
          </div>
        </div>
      );
    }


    <button disabled={loading}>
      {loading ? <span className="btn-spinner" /> : "Add to cart"}
    </button>

    return (
      <div className="page">
        <Helmet>
          <title>{`${selectedProduct.name} | 164 Diecast Zone`}</title>
          <meta
            name="description"
            content={
              (Array.isArray(selectedProduct.details)
                ? selectedProduct.details.join(" ")
                : String(selectedProduct.details || "")
              ).slice(0, 160) ||
              `Buy ${selectedProduct.name} online at 164 Diecast Zone — premium 1:64 diecast model cars with fast shipping.`
            }
          />
          <link
            rel="canonical"
            href={`https://164diecastzone.com/category/${selectedProduct.brand}/product/${selectedProduct.id}`}
          />
          <meta property="og:title" content={`${selectedProduct.name} | 164 Diecast Zone`} />
          <meta
            property="og:description"
            content={`Buy ${selectedProduct.name} online at 164 Diecast Zone — premium 1:64 diecast model cars with fast shipping.`}
          />
          {selectedProduct.image && <meta property="og:image" content={selectedProduct.image} />}
          <meta
            property="og:url"
            content={`https://164diecastzone.com/category/${selectedProduct.brand}/product/${selectedProduct.id}`}
          />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              name: selectedProduct.name,
              image: selectedProduct.image ? [selectedProduct.image] : undefined,
              brand: selectedProduct.brand
                ? { "@type": "Brand", name: selectedProduct.brand }
                : undefined,
              offers: {
                "@type": "Offer",
                priceCurrency: "INR",
                price: selectedProduct.price,
                availability: "https://schema.org/InStock",
                url: `https://164diecastzone.com/category/${selectedProduct.brand}/product/${selectedProduct.id}`,
              },
            })}
          </script>
        </Helmet>
        <div className="breadcrumb11">
          <div className="page-container">
            <span onClick={() => navigate("/")}>Home</span>
            <span className="highlight"> / </span>
            <span
              className="breadcrumb-link"
              onClick={() => navigate(`/category/${selectedProduct.brand}`)}
            >
              {selectedProduct.brand}
            </span>
            <span className="highlight"> / </span>
            {selectedProduct.name}
          </div>
        </div>

        <div className="detail-content">
          <div className="detail-grid">
            <div className="pd-media">
              <div className="pd-thumbnails">
                {images.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`thumb-${idx}`}
                    className={selectedImage === idx ? "active" : ""}
                    onClick={() => setSelectedImage(idx)}
                  />
                ))}
              </div>
              <div className="pd-main-image">
                <img
                  src={images[selectedImage] || selectedProduct.image}
                  alt={selectedProduct.name}
                />
              </div>


            </div>

            <div className="pd-info">
              <div className="pd-brand">{selectedProduct.brand}</div>
              <h1 className="pd-title">{selectedProduct.name}</h1>

              <div className="pd-price-row">
                <span className="pd-price">₹{selectedProduct.price}</span>

              </div>

              {showSelectedPointsBadge && (
                <div className="pd-points">Earn ₹{selectedPoints} Rewards on this purchase</div>
              )}

              <div className="pd-actions">
                {isInCart && !isSoldOut && (
                  <div className="pd-qty">
                    <button
                      onClick={() =>
                        updateCart({
                          type: "product",
                          id: selectedProduct.id,
                          delta: -1,
                        })
                      }
                    >
                      -
                    </button>
                    <span>{cartQty}</span>
                    <button
                      onClick={() =>
                        updateCart({
                          type: "product",
                          id: selectedProduct.id,
                          delta: 1,
                        })
                      }
                      disabled={cartQty >= maxUnit}
                    >
                      +
                    </button>
                  </div>
                )}

                {!isSoldOut && !isInCart && (
                  <button
                    className="pd-add-cart"
                    onClick={() => handleAddToCart()}
                    disabled={isAdding}
                  >
                    {isAdding ? <span className="btn-spinner" /> : "Add To Cart"}
                  </button>
                )}

                {isSoldOut && (
                  <button className="pd-soldout" disabled>
                    SOLD OUT
                  </button>
                )}

                <button
                  className={`pd-fav ${isFavorite(selectedProduct.id) ? "active" : ""
                    }`}
                  onClick={() => toggleFavorite(selectedProduct.id)}
                >
                  <Heart
                    size={22}
                    fill={isFavorite(selectedProduct.id) ? "currentColor" : "none"}
                  />
                </button>

                <ShareButton
                  title={selectedProduct.name}
                  text={`Check out ${selectedProduct.name} on DiecastZone`}
                />
              </div>

              <div className="pd-details">
                <h3>Product Details</h3>
                <ul>
                  {normalizedDetails.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="related-section">
            <div className="container">
              <div className="related-header">
                <h2 className="related-title">
                  RELATED <span className="related-accent">PRODUCTS</span>
                </h2>


              </div>

              <div className="related-grid">
                {relatedProducts.length > 0 ? (
                  relatedProducts.map((product) => {
                    const mrp = product.mrp;
                    const price = product.price;
                    const displayPoints = resolveDisplayPoints(product, isMember);
                    const currentQty = getProductQuantity(product.id);
                    const isInCart = isProductInCart(product.id);
                    const maxUnit = product.max_unit ?? 0;
                    const isSoldOut = maxUnit === 0;
                    const isMaxReached = maxUnit > 0 && currentQty >= maxUnit;
                    const showRelatedPointsBadge = displayPoints > 0;
                    const discount =
                      mrp && mrp > price
                        ? Math.round(((mrp - price) / mrp) * 100)
                        : 0;

                    return (
                      <div key={product.id} className="related-card">
                        <div className="related-image-wrapper">
                          {product.is_new_arrival ? (
                            <div className="related-badge">New Arrival</div>
                          ) : product.is_trending ? (
                            <div className="related-badge">Trending Now</div>
                          ) : null}

                          {showRelatedPointsBadge && (
                            <div className="related-points-badge">₹{displayPoints} Rewards</div>
                          )}

                          <img
                            src={product.image}
                            alt={product.name}
                            className="related-image"
                            onClick={() =>
                              navigate(`../product/${product.id}`)
                            }
                          />
                        </div>

                        <div className="related-info">
                          <h3
                            className="related-name"
                            onClick={() => navigate(`../product/${product.id}`)}
                          >
                            {product.name}
                          </h3>

                          <div className="related-brand">
                            {product.brandLabel || product.brand} {product.variant}
                          </div>

                          {isMember && mrp && mrp > price ? (
                            <>
                              <div className="related-mrp-row">
                                <span className="related-mrp-label">MRP:</span>
                                <span className="related-mrp">₹{mrp}</span>
                              </div>

                              <div className="related-price-row">
                                <div className="related-price">₹{price}</div>
                                {discount >= 10 && (
                                  <span className="related-discount">
                                    {discount}% OFF
                                  </span>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="related-price-row">
                              <div className="related-price">₹{price}</div>
                            </div>
                          )}

                          <div className="related-action-row">
                            {isSoldOut ? (
                              <button className="related-cart-btn related-cart-btn-disabled" disabled>
                                Sold Out
                              </button>
                            ) : !isInCart ? (
                              <button
                                onClick={() => handleAddToCart(product)}
                                className="related-cart-btn"
                              >
                                <ShoppingCart size={16} />
                                Add To Cart
                              </button>
                            ) : (
                              <div className="related-qty">
                                <button
                                  onClick={() =>
                                    updateCart({
                                      type: "product",
                                      id: product.id,
                                      delta: -1,
                                    })
                                  }
                                >
                                  -
                                </button>
                                <span>{currentQty}</span>
                                <button
                                  onClick={() =>
                                    updateCart({
                                      type: "product",
                                      id: product.id,
                                      delta: 1,
                                    })
                                  }
                                  disabled={isMaxReached}
                                >
                                  +
                                </button>
                              </div>
                            )}

                            <button
                              className={`related-fav-btn related-fav-btn-inline ${isFavorite(product.id) ? "active" : ""}`}
                              onClick={() => toggleFavorite(product.id)}
                            >
                              <Heart size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: "20px" }}>
                    No related products found
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  return (
    <Routes>
      <Route path="/" element={<ProductListing key={selectedBrand} />} />
      <Route
        path="product/:productId"
        element={
          <ProductDetail
            products={products}
            loading={loading}
          />
        }
      />

    </Routes>

  );


};

export default DiecastStore;
